// src/components/ai/AiWeatherCommandCenter.js
// --- UPDATE: new component — AI weather & flood intelligence panel for LiveReadingsPage
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, HStack, VStack, Text, Icon, Badge, Button, IconButton, Tooltip,
  SimpleGrid, Skeleton, SkeletonText, Collapse, CircularProgress, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAutoAwesome, MdRefresh, MdExpandMore, MdExpandLess, MdWbSunny, MdWbCloudy,
  MdCloudQueue, MdGrain, MdFlashOn, MdTimer, MdTrendingUp, MdTrendingDown,
  MdTrendingFlat, MdSensors, MdSensorsOff, MdMemory, MdCloudDone, MdChevronRight,
} from 'react-icons/md';

const COLLAPSE_KEY = 'flowsense.aiCommandCenter.collapsed';

const TIERS = [
  { key: 'critical', label: 'Critical', color: 'red.500' },
  { key: 'high', label: 'High', color: 'orange.400' },
  { key: 'watch', label: 'Watch', color: 'yellow.400' },
  { key: 'stable', label: 'Stable', color: 'green.400' },
];

const TIER_SCHEME = { critical: 'red', high: 'orange', watch: 'yellow', stable: 'green' };

const weatherIcon = (code) => {
  const n = Number(code);
  if (code === null || code === undefined || !Number.isFinite(n)) return MdCloudQueue;
  if (n === 0) return MdWbSunny;
  if (n <= 3) return MdWbCloudy;
  if (n >= 95) return MdFlashOn;
  if (n >= 51) return MdGrain;
  return MdCloudQueue;
};

const rainRisk = (mm) => {
  const v = Number(mm);
  if (!Number.isFinite(v)) return { level: 'UNKNOWN', scheme: 'gray' };
  if (v >= 50) return { level: 'SEVERE', scheme: 'red' };
  if (v >= 25) return { level: 'HIGH', scheme: 'orange' };
  if (v >= 10) return { level: 'MODERATE', scheme: 'yellow' };
  return { level: 'LOW', scheme: 'green' };
};

const ago = (ts, now = Date.now()) => {
  if (!ts) return null;
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
};

const fmtEta = (m) => {
  if (m === null || m === undefined || !Number.isFinite(Number(m))) return null;
  if (m <= 0) return 'now';
  if (m < 60) return `~${Math.round(m)}m`;
  return `~${Math.floor(m / 60)}h ${Math.round(m % 60)}m`;
};

const RainBars = memo(({ hourly = [], emptyColor }) => {
  const max = useMemo(() => hourly.reduce((m, h) => Math.max(m, h.precipMm), 1), [hourly]);
  if (!hourly.length) return null;
  return (
    <Box>
      <Flex align="flex-end" h="38px" gap="2px" role="img" aria-label="Rainfall forecast, next 24 hours">
        {hourly.map((h) => (
          <Tooltip
            key={h.time}
            hasArrow
            openDelay={100}
            label={`${new Date(h.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · ${h.precipMm.toFixed(1)} mm${h.prob !== null ? ` · ${h.prob}%` : ''}`}
          >
            <Box flex="1" h="100%" position="relative" display="flex" alignItems="flex-end">
              {h.prob !== null && (
                <Box position="absolute" bottom={0} left={0} right={0} h={`${h.prob}%`} bg="purple.300" opacity={0.16} borderRadius="1px" />
              )}
              <Box
                w="100%"
                h={`${Math.max(h.precipMm > 0 ? 8 : 3, (h.precipMm / max) * 100)}%`}
                bg={h.precipMm > 0 ? 'blue.400' : emptyColor}
                opacity={h.precipMm > 0 ? 0.9 : 0.45}
                borderRadius="1px"
              />
            </Box>
          </Tooltip>
        ))}
      </Flex>
      <Flex justify="space-between" mt={1} fontSize="9px" color="gray.500">
        <Text>Now</Text><Text>+12h</Text><Text>+24h</Text>
      </Flex>
    </Box>
  );
});
RainBars.displayName = 'RainBars';

const Stat = ({ label, value, unit, color, subtle }) => (
  <Box>
    <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color={subtle}>{label}</Text>
    <Text fontSize="md" fontWeight="800" color={color} lineHeight="1.2">
      {value ?? '—'}
      {value !== null && value !== undefined && unit && (
        <Text as="span" fontSize="2xs" fontWeight="600" ml={0.5}>{unit}</Text>
      )}
    </Text>
  </Box>
);

const AiWeatherCommandCenter = ({
  weather,
  weatherStatus = {},
  insights,
  isConnected,
  serverStream = {},
  onReloadWeather,
  onSelectWell,
}) => {
  const panelBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const sectionBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rowHover = useColorModeValue('purple.50', 'whiteAlpha.100');
  const emptyBar = useColorModeValue('gray.300', 'gray.600');
  const accentGradient = useColorModeValue(
    'linear(to-r, purple.500, blue.500, cyan.400)',
    'linear(to-r, purple.400, blue.400, cyan.300)'
  );
  const narrativeBg = useColorModeValue('purple.50', 'rgba(159, 122, 234, 0.12)');

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const counts = insights?.counts || { critical: 0, high: 0, watch: 0, stable: 0 };
  const total = Math.max(1, insights?.total || 0);
  const risk = rainRisk(weather?.next24hRainMm);
  const WIcon = weatherIcon(weather?.current?.weatherCode);
  const serverLive = !!serverStream.lastAt && now - serverStream.lastAt < 15 * 60 * 1000;

  return (
    <Box
      bg={panelBg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="2xl"
      boxShadow="md"
      mb={6}
      overflow="hidden"
      position="relative"
    >
      <Box h="3px" bgGradient={accentGradient} />

      {/* Header */}
      <Flex px={5} py={3} align="center" justify="space-between" gap={3} flexWrap="wrap">
        <HStack spacing={3}>
          <Flex boxSize="36px" borderRadius="xl" bgGradient={accentGradient} align="center" justify="center" boxShadow="sm">
            <Icon as={MdAutoAwesome} color="white" boxSize={5} />
          </Flex>
          <Box>
            <Text fontSize="sm" fontWeight="800">AI Weather & Flood Intelligence</Text>
            <Text fontSize="xs" color={subtle} noOfLines={1}>{insights?.narrative}</Text>
          </Box>
        </HStack>

        <HStack spacing={2} flexWrap="wrap">
          {counts.critical > 0 && (
            <Badge colorScheme="red" variant="solid" borderRadius="full" px={2}>
              {counts.critical} CRITICAL
            </Badge>
          )}
          <Tooltip hasArrow label={serverLive ? `Last server prediction ${ago(serverStream.lastAt, now)} · ${serverStream.count} received this session` : 'No recent server ML push — edge model is filling gaps'}>
            <Badge colorScheme={serverLive ? 'purple' : 'gray'} variant="subtle" borderRadius="full" px={2} display="flex" alignItems="center" gap={1}>
              <Icon as={MdCloudDone} boxSize={3} /> SERVER ML {serverLive ? 'LIVE' : 'IDLE'}
            </Badge>
          </Tooltip>
          <Tooltip hasArrow label="In-browser model using water-level trend, rainfall forecast and wellbore clearance">
            <Badge colorScheme="cyan" variant="subtle" borderRadius="full" px={2} display="flex" alignItems="center" gap={1}>
              <Icon as={MdMemory} boxSize={3} /> EDGE {insights?.edgeCount || 0}
            </Badge>
          </Tooltip>
          <Tooltip hasArrow label={isConnected ? 'Real-time WebSocket connected' : 'WebSocket disconnected — predictions may be stale'}>
            <Badge colorScheme={isConnected ? 'green' : 'red'} variant="subtle" borderRadius="full" px={2} display="flex" alignItems="center" gap={1}>
              <Icon as={isConnected ? MdSensors : MdSensorsOff} boxSize={3} /> {isConnected ? 'WS LIVE' : 'WS DOWN'}
            </Badge>
          </Tooltip>
          <IconButton
            size="sm"
            variant="ghost"
            icon={collapsed ? <MdExpandMore /> : <MdExpandLess />}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand AI panel' : 'Collapse AI panel'}
            aria-expanded={!collapsed}
          />
        </HStack>
      </Flex>

      <Collapse in={!collapsed} animateOpacity>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} px={5} pb={5}>
          {/* Weather */}
          <Box p={4} borderRadius="xl" bg={sectionBg} borderWidth="1px" borderColor={border}>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color={subtle}>
                Weather · {weatherStatus.location?.label || '—'}
              </Text>
              <HStack spacing={1}>
                <Badge colorScheme={risk.scheme} borderRadius="full" fontSize="9px">{risk.level}</Badge>
                <Tooltip hasArrow label={`${weatherStatus.source || 'No source'}${weatherStatus.fetchedAt ? ` · ${ago(weatherStatus.fetchedAt, now)}` : ''}${weatherStatus.isServerPushed ? ' · pushed over WebSocket' : ''}`}>
                  <IconButton size="xs" variant="ghost" icon={<MdRefresh />} isLoading={weatherStatus.isLoading} onClick={onReloadWeather} aria-label="Refresh weather" />
                </Tooltip>
              </HStack>
            </Flex>
            {!weather ? (
              weatherStatus.error ? (
                <VStack align="start" spacing={2}>
                  <Text fontSize="xs" color={subtle}>Weather feed unavailable — the flood model runs without rainfall input.</Text>
                  <Button size="xs" variant="outline" leftIcon={<MdRefresh />} onClick={onReloadWeather}>Retry</Button>
                </VStack>
              ) : (
                <VStack align="stretch" spacing={3}>
                  <Skeleton h="28px" w="60%" borderRadius="md" />
                  <SkeletonText noOfLines={2} spacing={2} skeletonHeight="2" />
                  <Skeleton h="38px" borderRadius="md" />
                </VStack>
              )
            ) : (
              <>
                <HStack spacing={3} mb={3}>
                  <Icon as={WIcon} boxSize={8} color={WIcon === MdWbSunny ? 'yellow.400' : WIcon === MdFlashOn ? 'purple.400' : 'blue.400'} />
                  <Box>
                    <Text fontSize="2xl" fontWeight="900" lineHeight="1">
                      {weather.current?.temperatureC !== null && weather.current?.temperatureC !== undefined ? `${Math.round(weather.current.temperatureC)}°C` : '—'}
                    </Text>
                    <Text fontSize="xs" color={subtle}>
                      {weather.current?.label || '—'}
                      {weather.current?.windKph !== null && weather.current?.windKph !== undefined ? ` · ${Math.round(weather.current.windKph)} km/h` : ''}
                    </Text>
                  </Box>
                </HStack>
                <SimpleGrid columns={4} spacing={2} mb={3}>
                  <Stat label="6h" value={weather.next6hRainMm} unit="mm" color="blue.400" subtle={subtle} />
                  <Stat label="24h" value={weather.next24hRainMm} unit="mm" color="blue.500" subtle={subtle} />
                  <Stat label="Past" value={weather.past24hRainMm} unit="mm" subtle={subtle} />
                  <Stat label="Peak" value={weather.peakPrecipProbability} unit="%" color="purple.400" subtle={subtle} />
                </SimpleGrid>
                <RainBars hourly={weather.hourly || []} emptyColor={emptyBar} />
              </>
            )}
          </Box>

          {/* Fleet risk */}
          <Box p={4} borderRadius="xl" bg={sectionBg} borderWidth="1px" borderColor={border}>
            <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color={subtle} mb={3}>
              Fleet flood risk · {insights?.total || 0}/{insights?.wellCount || 0} wells evaluated
            </Text>
            <SimpleGrid columns={4} spacing={2} mb={3}>
              {TIERS.map((t) => (
                <Box key={t.key} textAlign="center">
                  <Text fontSize="xl" fontWeight="900" color={counts[t.key] > 0 ? t.color : subtle}>{counts[t.key]}</Text>
                  <Text fontSize="9px" fontWeight="700" textTransform="uppercase" color={subtle}>{t.label}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Flex h="8px" borderRadius="full" overflow="hidden" bg={emptyBar} mb={3} role="img" aria-label="Risk distribution">
              {TIERS.map((t) => counts[t.key] > 0 && (
                <Box key={t.key} w={`${(counts[t.key] / total) * 100}%`} bg={t.color} transition="width 0.4s ease" />
              ))}
            </Flex>
            <HStack justify="space-between" fontSize="xs">
              <HStack spacing={2}>
                {insights?.avgConfidence !== null && insights?.avgConfidence !== undefined && (
                  <>
                    <CircularProgress value={Math.round(insights.avgConfidence * 100)} size="18px" thickness="14px" color="purple.400" />
                    <Text fontWeight="700">{Math.round(insights.avgConfidence * 100)}% avg confidence</Text>
                  </>
                )}
              </HStack>
              <Text color={subtle}>{insights?.serverCount || 0} server · {insights?.edgeCount || 0} edge</Text>
            </HStack>
            {insights?.earliest && (
              <HStack mt={3} spacing={2} p={2} borderRadius="lg" bg={insights.earliest.eta <= 60 ? 'rgba(229,62,62,0.10)' : 'transparent'}>
                <Icon as={MdTimer} color={insights.earliest.eta <= 60 ? 'red.500' : subtle} />
                <Text fontSize="xs" fontWeight="700">
                  Earliest overtopping {fmtEta(insights.earliest.eta)} · {insights.earliest.well.name || `Well ${insights.earliest.well.id}`}
                </Text>
              </HStack>
            )}
            <HStack mt={3} align="start" spacing={2} p={2} borderRadius="lg" bg={narrativeBg}>
              <Icon as={MdAutoAwesome} color="purple.400" mt="2px" />
              <Text fontSize="xs" fontWeight="600">{insights?.narrative}</Text>
            </HStack>
          </Box>

          {/* Top risk */}
          <Box p={4} borderRadius="xl" bg={sectionBg} borderWidth="1px" borderColor={border}>
            <Text fontSize="10px" fontWeight="800" textTransform="uppercase" letterSpacing="0.08em" color={subtle} mb={2}>
              Highest-risk wells
            </Text>
            {!insights?.top?.length ? (
              <Text fontSize="xs" color={subtle} py={4}>
                No predictions yet. Wells are evaluated once a linked water-level sensor reports or the ML service pushes a prediction.
              </Text>
            ) : (
              <VStack align="stretch" spacing={1}>
                {insights.top.map(({ well, prediction, tier, probability }) => {
                  const TrendI = prediction.trend === 'rising' ? MdTrendingUp : prediction.trend === 'falling' ? MdTrendingDown : MdTrendingFlat;
                  const eta = fmtEta(prediction.estimatedMinutesToOvertopping);
                  return (
                    <Flex
                      key={String(well.id)}
                      as={onSelectWell ? 'button' : 'div'}
                      onClick={onSelectWell ? () => onSelectWell(well) : undefined}
                      align="center"
                      gap={2}
                      p={2}
                      borderRadius="lg"
                      textAlign="left"
                      w="100%"
                      _hover={onSelectWell ? { bg: rowHover } : undefined}
                      aria-label={`${well.name || `Well ${well.id}`}, ${Math.round(probability * 100)}% flood risk`}
                    >
                      <Box w="4px" alignSelf="stretch" borderRadius="full" bg={`${TIER_SCHEME[tier] || 'gray'}.400`} />
                      <Box flex="1" minW={0}>
                        <Text fontSize="sm" fontWeight="700" noOfLines={1}>{well.name || `Well ${well.id}`}</Text>
                        <HStack spacing={2} fontSize="10px" color={subtle}>
                          <Badge fontSize="8px" colorScheme={prediction.source === 'edge' ? 'cyan' : 'purple'} variant="subtle">
                            {prediction.source === 'edge' ? 'EDGE' : 'SERVER'}
                          </Badge>
                          {eta && <Text fontWeight="700" color={prediction.estimatedMinutesToOvertopping <= 60 ? 'red.500' : undefined}>overtop {eta}</Text>}
                          {prediction.serverStale && <Text color="orange.400">server stale</Text>}
                        </HStack>
                      </Box>
                      <Icon as={TrendI} color={prediction.trend === 'rising' ? 'red.400' : prediction.trend === 'falling' ? 'green.400' : 'gray.400'} />
                      <Text fontSize="sm" fontWeight="900" color={`${TIER_SCHEME[tier] || 'gray'}.500`} minW="40px" textAlign="right">
                        {Math.round(probability * 100)}%
                      </Text>
                      {onSelectWell && <Icon as={MdChevronRight} color={subtle} />}
                    </Flex>
                  );
                })}
              </VStack>
            )}
          </Box>
        </SimpleGrid>
        <Text fontSize="9px" color={subtle} px={5} pb={3}>
          Server ML predictions take precedence while fresh; edge estimates fill gaps and are labelled. Verify on site before operational decisions.
        </Text>
      </Collapse>
    </Box>
  );
};

export default memo(AiWeatherCommandCenter);
// --- END UPDATE